<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] p-4 pb-24">
    <!-- LOADING -->
    <Loading v-if="loading"
             :text="$t('views.famacha.loading')" />

    <!-- ERROR -->
    <div v-else-if="error"
         class="text-red-600 text-center">
      {{ error }}
    </div>

    <!-- CONTENT -->
    <div v-else
         class="space-y-6">

      <!-- List of existing FAMACHA values -->
      <FamachaList :famacha="famacha"
                  :goatId="goatId"
                  :refreshFamacha="refreshFamacha" />
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useFamachaStore } from '@/stores/famacha.store.js'
import { useGoatStore } from '@/stores/goat.store.js'
import { useUiStore } from '@/stores/ui.store.js'
import { storeToRefs } from 'pinia'
import Loading from '@/components/ui/feedback/Loading.vue'
import FamachaList from '@/components/goats/lists/FamachaList.vue'

const { t } = useI18n()
const route = useRoute()
const goatId = Number(route.params.id)

const famachaStore = useFamachaStore()
const goatStore = useGoatStore()
const uiStore = useUiStore()
const { famachaChecks: famacha, loading, error } = storeToRefs(famachaStore)

const goat = computed(() => goatStore.goats.find((g) => g.id === goatId))

onMounted(async () => {
  if (goatStore.goats.length === 0) {
    await goatStore.fetchGoats()
  }
  await famachaStore.fetchFamachaChecks(goatId)
})

watch(goat, (g) => {
  uiStore.setHeader({
    title: t('views.selectedGoat.famacha.title'),
    subtitle: g?.eartag || `#${goatId}`,
    showBack: true
  })
}, { immediate: true })

function refreshFamacha() {
  famachaStore.fetchFamachaChecks(goatId)
}
</script>
